/* eslint-disable @typescript-eslint/no-unused-vars */
import { AxiosResponse } from "axios";
import {
  GetAllModelTypeResponse,
  GetAllPartsApiListResponse,
  GetPartDetailsByIdResponse,
  InventoryPartDataResponse,
  PartFamily,
  PartFamilyResponse,
} from "../../@types/DRO/GetPartsListResponse";
import {
  ProductPartsBody,
  UpdatePartsBody,
} from "../../@types/DTO/AddPartsBody";
import { PurchaseOrderBody } from "../../@types/DTO/CreatePurchaseOrderBody";
import { erpAxios, getAuthToken } from "../axiosInstances";

const getHeaderValue = (headerValue: unknown, fallback = "") => {
  if (typeof headerValue === "string") {
    return headerValue;
  }

  if (Array.isArray(headerValue)) {
    return headerValue.join(", ");
  }

  return fallback;
};

// Serializes params as repeated keys: warehouseId=1&warehouseId=2&warehouseId=3
// (no [] and no [0]/[1] indices). Works regardless of axios version, since
// axios's built-in `{ indexes: false }` object form isn't reliable across
// versions for this exact format.
const repeatKeyParamsSerializer = (params: Record<string, unknown>) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === undefined || item === null) return;
        searchParams.append(key, String(item));
      });
    } else {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

export class AllPartApi {
  public static api = new AllPartApi();
  private instance = erpAxios;

  getToken() {
    return getAuthToken();
  }
  private downloadFile(response: AxiosResponse<Blob>, defaultFileName: string) {
    try {
      // Validate response
      if (!response.data) {
        throw new Error("No file data received from server");
      }

      const blob = new Blob([response.data], {
        type: getHeaderValue(
          response.headers["content-type"],
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ),
      });

      // Get filename from Content-Disposition header if available
      const contentDisposition = getHeaderValue(
        response.headers["content-disposition"]
      );
      let filename = `${defaultFileName}-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        if (document.body.contains(link)) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error in downloadFile:", error);
      throw error;
    }
  }

  async getPartList(
    page?: number,
    pageSize?: number,
    search?: string,
    startDate?: string,
    endDate?: string,
    modelTypeId?: number,
    warehouseId?: number,
    purchaseOrderId?: number,
    includeOutOfStockParts?: boolean,
    showDisabled?: boolean,
    partTypeId?: number
  ): Promise<GetAllPartsApiListResponse> {
    try {
      const response: AxiosResponse<GetAllPartsApiListResponse> =
        await this.instance.get("/erp/api/v1/parts/all", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            pageSize,
            search,
            startDate,
            endDate,
            modelTypeId,
            warehouseId,
            purchaseOrderId,
            includeOutOfStockParts,
            showDisabled,
            partTypeId,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching predictions all data", error);
      throw error;
    }
  }

  async getInventoryPartList(
    page?: number,
    pageSize?: number,
    search?: string,
    warehouseId?: number | number[],
    type?: string
  ): Promise<InventoryPartDataResponse> {
    try {
      const warehouseIds =
        warehouseId === undefined
          ? undefined
          : Array.isArray(warehouseId)
          ? warehouseId
          : [warehouseId];

      const response: AxiosResponse<InventoryPartDataResponse> =
        await this.instance.get("/erp/api/v1/parts/inventory/all", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            pageSize,
            search,
            warehouseId: warehouseIds,
            type,
          },
          paramsSerializer: repeatKeyParamsSerializer,
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching predictions all data", error);
      throw error;
    }
  }

  async getPartsDetailById(id: number): Promise<GetPartDetailsByIdResponse> {
    try {
      const response: AxiosResponse<GetPartDetailsByIdResponse> =
        await this.instance.get(`/erp/api/v1/parts/${id}`, {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            id,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching predictions all data", error);
      throw error;
    }
  }
  async getModelTypeAll(): Promise<GetAllModelTypeResponse> {
    try {
      const response: AxiosResponse<GetAllModelTypeResponse> =
        await this.instance.get("/erp/api/v1/parts/model-type/all", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching predictions latest data", error);
      throw error;
    }
  }

  async getPartType(): Promise<GetAllModelTypeResponse> {
    try {
      const response: AxiosResponse<GetAllModelTypeResponse> =
        await this.instance.get("/erp/api/v1/parts/part-type/all", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching predictions latest data", error);
      throw error;
    }
  }

  async getPartFamilies(page = 0, size = 10): Promise<PartFamily[]> {
    try {
      const response: AxiosResponse<PartFamilyResponse> = await this.instance.get(
        "/erp/api/v1/parts/family",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: { page, size },
        }
      );

      const families = response.data.data?.partFamily;
      if (!Array.isArray(families)) {
        throw new Error("Invalid part family response: data.partFamily is missing");
      }

      return families;
    } catch (error) {
      console.error("Error fetching part families", error);
      throw error;
    }
  }

  async addParts(orderBody: ProductPartsBody): Promise<void> {
    try {
      await this.instance.post(`/erp/api/v1/parts/add`, orderBody, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });
      console.log("Purchase order created successfully");
    } catch (error) {
      console.error("Error creating purchase order", error);
      throw error;
    }
  }
  async updatePartsStatus(
    id: string,
    orderBody: PurchaseOrderBody
  ): Promise<void> {
    try {
      await this.instance.put(
        `/erp/api/v1/purchase-order/update?id=${id}`,
        orderBody,
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        }
      );
      console.log("Purchase order updated successfully");
    } catch (error) {
      console.error("Error updating purchase order", error);
      throw error;
    }
  }
  async changePartStatus(id: string, status: string): Promise<void> {
    try {
      await this.instance.put(
        `/erp/api/v1/parts/status-change/${id}/${status}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        }
      );
      console.log("Part status changed successfully");
    } catch (error) {
      console.error("Error changing part status", error);
      throw error;
    }
  }
  async getInventoryPartListReportDownload(
    warehouseId?: number | number[],
    search?: string,
    type?: string
  ): Promise<void> {
    try {
      const warehouseIds =
        warehouseId === undefined
          ? undefined
          : Array.isArray(warehouseId)
          ? warehouseId
          : [warehouseId];

      console.log(
        "Downloading report for warehouseId:",
        warehouseIds,
        "search:",
        search
      );

      const response: AxiosResponse<Blob> = await this.instance.get(
        "/erp/api/v1/parts/inventory/report/download",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*",
          },
          params: {
            warehouseId: warehouseIds,
            search,
            type,
          },
          paramsSerializer: repeatKeyParamsSerializer,
          responseType: "blob",
        }
      );

      console.log("Response received:", {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers["content-type"],
        blobSize: response.data?.size,
        headers: response.headers,
      });

      if (response.status >= 400) {
        let errorMessage = `Failed to download report (Status: ${response.status})`;

        const contentType = getHeaderValue(response.headers["content-type"]);
        if (
          contentType.includes("application/json") ||
          contentType.includes("text/plain") ||
          contentType.includes("text/html")
        ) {
          try {
            const text = await response.data.text();
            console.log("Error response text:", text);
            try {
              const jsonData = JSON.parse(text);
              errorMessage =
                jsonData.message ||
                jsonData.error ||
                jsonData.errorMessage ||
                errorMessage;
            } catch {
              if (text && text.length < 500) {
                errorMessage = text;
              }
            }
          } catch (parseError) {
            console.error("Error parsing error response", parseError);
          }
        }

        throw new Error(errorMessage);
      }

      if (!response.data) {
        throw new Error(
          "No file data received from server. Please check if the report has any data."
        );
      }

      if (response.data.size === 0) {
        console.warn("Empty file received from server. Size: 0 bytes");
      }

      console.log("Attempting to download file, size:", response.data.size);
      this.downloadFile(response, `inventory-report${type ? `-${type}` : ""}`);
    } catch (error: any) {
      console.error("Error downloading inventory report - Full error:", {
        error,
        message: error?.message,
        code: error?.code,
        response: error?.response,
        status: error?.response?.status,
        data: error?.response?.data,
      });

      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          console.log("Error blob text:", text);
          try {
            const jsonData = JSON.parse(text);
            throw new Error(
              jsonData.message ||
                jsonData.error ||
                jsonData.errorMessage ||
                `Failed to download report (Status: ${error.response.status})`
            );
          } catch {
            if (text && text.length < 500) {
              throw new Error(text);
            }
          }
        } catch (parseError) {
          console.error("Error parsing error blob:", parseError);
        }
      }

      if (error?.code === "ERR_NETWORK") {
        const requestUrl = error?.config?.url || error?.request?.responseURL || "";
        const isWarehouse396 = requestUrl.includes("warehouseId=396");

        if (isWarehouse396) {
          throw new Error(
            "Backend Error: The server is sending duplicate Content-Disposition headers for warehouse ID 396, which prevents the browser from downloading the file. This is a backend configuration issue that needs to be fixed on the server side. The backend team needs to remove the duplicate Content-Disposition header from the response."
          );
        }

        throw new Error(
          "Network error occurred while downloading the report. This may be due to invalid response headers from the server. Please try again or contact support if the issue persists."
        );
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error(
        error?.response?.data?.message ||
          error?.message ||
          `Failed to download report${error?.response?.status ? ` (Status: ${error.response.status})` : ""}. Please try again.`
      );
    }
  }
  async updatePartsInfo(id: string, orderBody: UpdatePartsBody): Promise<void> {
    try {
      await this.instance.put(`/erp/api/v1/parts/${id}`, orderBody, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });
    } catch (error) {
      throw error;
    }
  }
  async warehouseStockUpdate(
    id: number,
    partID: number,
    quantity: number,
    partCondition?: string
  ): Promise<void> {
    try {
      await this.instance.put(
        `/erp/api/v1/warehouse-stock/update`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            id,
            partId: partID,
            quantity,
            partCondition,
          },
        }
      );
      console.log("Part status changed successfully");
    } catch (error) {
      console.error("Error changing part status", error);
      throw error;
    }
  }

  async downloadSampleSheet(): Promise<void> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "/erp/api/v1/parts/sample/sheet",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          responseType: "blob",
        }
      );

      this.downloadFile(response, "parts-sample-sheet");
    } catch (error) {
      console.error("Error downloading sample sheet", error);
      throw error;
    }
  }

  async uploadBulkParts(file: File): Promise<{ success: boolean; message?: string; hasErrorFile?: boolean }> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await this.instance.post("/erp/api/v1/parts/bulk/upload", formData, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
          "Content-Type": "multipart/form-data",
        },
        responseType: "blob",
      });

      const contentType = getHeaderValue(response.headers["content-type"]);

      if (
        contentType?.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
        contentType?.includes("application/vnd.ms-excel") ||
        contentType?.includes("application/octet-stream")
      ) {
        this.downloadFile(response, "bulk-upload-errors");
        return {
          success: false,
          message: "Some records could not be processed. Please check the downloaded error file for details.",
          hasErrorFile: true,
        };
      }

      const text = await response.data.text();
      try {
        const jsonData = JSON.parse(text);
        console.log("Parts uploaded successfully");
        return {
          success: true,
          message: jsonData?.message || jsonData?.data || "Parts uploaded successfully!",
        };
      } catch {
        return {
          success: true,
          message: "Parts uploaded successfully!",
        };
      }
    } catch (error) {
      console.error("Error uploading parts", error);
      throw error;
    }
  }
}